import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * car24.chamcore.com – Login Page (Canvas Preview)
 * TailwindCSS required.
 * Replace image URLs in BG_IMAGES with your own assets if desired.
 */

type LoginPayload = {
    username: string;
    password: string;
};

type Props = {
    onLogin?: (payload: LoginPayload) => Promise<void> | void;
    brand?: string;
    subtitle?: string;
};

const svgToDataUri = (svg: string) => `data:image/svg+xml,${encodeURIComponent(svg)}`;

// NOTE: Canvas preview doesn't have your /public assets.
// These first backgrounds are inline SVG “car-theme” silhouettes,
// so you WILL see car shapes immediately in the preview.
// In production, replace /images/... with real files in /public/images.
const BG_IMAGES = [
    svgToDataUri(`
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900'>
      <defs>
        <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0' stop-color='#0b0b0c'/>
          <stop offset='1' stop-color='#111114'/>
        </linearGradient>
        <radialGradient id='r' cx='45%' cy='25%' r='70%'>
          <stop offset='0' stop-color='#ffffff' stop-opacity='0.10'/>
          <stop offset='1' stop-color='#000000' stop-opacity='0'/>
        </radialGradient>
        <filter id='blur' x='-20%' y='-20%' width='140%' height='140%'>
          <feGaussianBlur stdDeviation='2.2'/>
        </filter>
      </defs>
      <rect width='1600' height='900' fill='url(#g)'/>
      <rect width='1600' height='900' fill='url(#r)'/>
      <g opacity='0.55' filter='url(#blur)'>
        <path d='M310 560c35-70 95-130 185-155 120-33 320-33 440 0 80 22 145 68 185 155l40 20v75c0 24-20 44-44 44h-60c-12 52-58 90-114 90s-102-38-114-90H612c-12 52-58 90-114 90s-102-38-114-90h-60c-24 0-44-20-44-44v-75l74-20z'
          fill='#ffffff' fill-opacity='0.11'/>
        <path d='M525 445c22-30 52-54 88-66 96-33 278-33 374 0 36 12 66 36 88 66H525z'
          fill='#ffffff' fill-opacity='0.10'/>
        <circle cx='520' cy='724' r='58' fill='#ffffff' fill-opacity='0.12'/>
        <circle cx='1080' cy='724' r='58' fill='#ffffff' fill-opacity='0.12'/>
      </g>
      <g opacity='0.22'>
        <path d='M220 730h1160' stroke='#ffffff' stroke-opacity='0.18' stroke-width='2'/>
      </g>
    </svg>
  `),
    svgToDataUri(`
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900'>
      <defs>
        <linearGradient id='g2' x1='0' y1='1' x2='1' y2='0'>
          <stop offset='0' stop-color='#070708'/>
          <stop offset='1' stop-color='#141416'/>
        </linearGradient>
        <filter id='glow' x='-30%' y='-30%' width='160%' height='160%'>
          <feGaussianBlur stdDeviation='5.5' result='b'/>
          <feMerge>
            <feMergeNode in='b'/>
            <feMergeNode in='SourceGraphic'/>
          </feMerge>
        </filter>
      </defs>
      <rect width='1600' height='900' fill='url(#g2)'/>
      <g opacity='0.45'>
        <path d='M250 610c70-120 170-190 310-215 140-25 370-25 510 0 140 25 240 95 310 215l55 25v70c0 26-21 47-47 47h-80c-10 55-58 97-119 97s-109-42-119-97H615c-10 55-58 97-119 97s-109-42-119-97h-80c-26 0-47-21-47-47v-70l80-25z'
          fill='#ffffff' fill-opacity='0.10' filter='url(#glow)'/>
        <path d='M575 450c38-40 92-70 158-80 114-18 262-18 376 0 66 10 120 40 158 80H575z'
          fill='#ffffff' fill-opacity='0.08'/>
        <path d='M520 520h560' stroke='#ffffff' stroke-opacity='0.12' stroke-width='8' stroke-linecap='round'/>
      </g>
      <g opacity='0.18'>
        <path d='M0 760c240-70 520-70 800 0s560 70 800 0v140H0V760z' fill='#ffffff' fill-opacity='0.06'/>
      </g>
    </svg>
  `),
    "/images/car-bg-1.jpg",
    "/images/car-bg-2.jpg",
    "/images/car-bg-3.jpg",
    "/images/car-bg-4.jpg",
];

export default function Car24Login({
    onLogin,
    brand = "car24",
    subtitle = "Logg inn for å administrere kjøp & leasing",
}: Props) {
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(true);
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<string | null>(localStorage.getItem("car24-username"));

    const { bgA, bgB } = useMemo(() => {
        // Use first two by default (inline SVG in canvas preview; swap to real /images in production)
        const a = BG_IMAGES[0];
        const b = BG_IMAGES[1] ?? BG_IMAGES[0];
        return { bgA: a, bgB: b };
    }, []);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);

        if (!username.trim() || !password) {
            setError("Skriv inn brukernavn og passord.");
            return;
        }

        try {
            setLoading(true);
            await onLogin?.({ username: username.trim(), password });
            navigate("/home"); // ✅ redirect after successful login
            // Demo feedback if no handler is wired
            if (!onLogin) {
                await new Promise((r) => setTimeout(r, 450));
                setError("Demo: Koble til ekte innlogging (onLogin) i appen.");
            }
        } catch (err: any) {
            setError(err?.message ?? "Innlogging feilet. Prøv igjen.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen w-full bg-black text-white relative overflow-hidden">
            {/* Background image layer */}
            <div className="absolute inset-0">
                {/* Base car image */}
                <div
                    className="absolute inset-0 bg-center bg-cover"
                    style={{
                        backgroundImage: `url(${bgA})`,
                        opacity: 0.34,
                        filter: "blur(1px) saturate(0.9) contrast(1.05)",
                        transform: "scale(1.05)",
                    }}
                />

                {/* Second car image (adds depth) */}
                <div
                    className="absolute inset-0 bg-center bg-cover"
                    style={{
                        backgroundImage: `url(${bgB})`,
                        opacity: 0.26,
                        filter: "blur(2px) saturate(0.85) contrast(1.1)",
                        transform: "scale(1.08)",
                        mixBlendMode: "lighten",
                    }}
                />

                {/* glossy dark overlays */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/80" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.12),transparent_55%)]" />
                {/* subtle vignette */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(0,0,0,0.88)_80%)]" />

                {/* faint diagonal shine */}
                <div className="absolute -inset-24 rotate-12 bg-gradient-to-r from-transparent via-white/6 to-transparent blur-2xl opacity-60" />
            </div>

            {/* Glossy blobs */}
            <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-white/8 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-40 -right-40 h-[30rem] w-[30rem] rounded-full bg-orange-500/10 blur-3xl" />

            {/* Content */}
            <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
                <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left brand panel */}
                    <div className="hidden lg:flex flex-col justify-center">
                        <div className="inline-flex items-center gap-3">
                            <div className="h-11 w-11 rounded-2xl bg-white/10 border border-white/10 backdrop-blur-md flex items-center justify-center">
                                <svg
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="opacity-90"
                                >
                                    <path
                                        d="M3 13.5V16a2 2 0 0 0 2 2h1"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M18 18h1a2 2 0 0 0 2-2v-2.5"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M6.5 18a2 2 0 1 0 4 0"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M13.5 18a2 2 0 1 0 4 0"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M21 13.5l-1.3-5.2a3 3 0 0 0-2.9-2.3H7.2a3 3 0 0 0-2.9 2.3L3 13.5"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M7 10h10"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>
                            <div className="flex flex-col">
                                <div className="text-3xl font-semibold tracking-tight">
                                    <span className="opacity-90">{brand}</span>
                                    <span className="opacity-60">.chamcore.com</span>
                                </div>
                                <div className="text-white/70 mt-1">{subtitle}</div>
                            </div>
                        </div>

                        <div className="mt-8 max-w-md">
                            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-6">
                                <div className="text-sm text-white/70">Hurtigoversikt</div>
                                <ul className="mt-3 space-y-2 text-white/80">
                                    <li className="flex items-center gap-2">
                                        <span className="h-1.5 w-1.5 rounded-full bg-orange-400/80" />
                                        Kjøp og leasing i samme miljø
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="h-1.5 w-1.5 rounded-full bg-orange-400/80" />
                                        Agent- og kundevisning
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="h-1.5 w-1.5 rounded-full bg-orange-400/80" />
                                        Rollebassert tilgang og audit
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Right login card */}
                    <div className="flex items-center justify-center">
                        <div className="w-full max-w-md">
                            <div className="rounded-[28px] border border-white/10 bg-white/6 backdrop-blur-2xl shadow-[0_20px_80px_rgba(0,0,0,0.55)] overflow-hidden">
                                {/* glossy header strip */}
                                <div className="relative p-6 pb-4">
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-white/5 to-transparent" />
                                    <div className="relative">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-xl font-semibold tracking-tight">Logg inn</div>
                                                <div className="text-sm text-white/65 mt-1">
                                                    Bruk konto fra Chamcore / car24
                                                </div>
                                            </div>
                                            <div className="h-10 w-10 rounded-2xl bg-black/30 border border-white/10 flex items-center justify-center">
                                                <span className="text-orange-300 font-semibold">24</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-6 pb-6">
                                    {error && (
                                        <div className="mb-4 rounded-2xl border border-orange-400/30 bg-orange-500/10 px-4 py-3 text-sm text-orange-100">
                                            {error}
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="text-sm text-white/70">Brukernavn</label>
                                            <input
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                                autoComplete="username"
                                                className="mt-2 w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/35 outline-none focus:border-orange-400/40 focus:ring-2 focus:ring-orange-400/15"
                                                placeholder="f.eks. stein@car24.no"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-sm text-white/70">Passord</label>
                                            <div className="mt-2 relative">
                                                <input
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    type={showPw ? "text" : "password"}
                                                    autoComplete="current-password"
                                                    className="w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-3 pr-12 text-white placeholder:text-white/35 outline-none focus:border-orange-400/40 focus:ring-2 focus:ring-orange-400/15"
                                                    placeholder="••••••••"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPw((s) => !s)}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl px-3 py-2 text-xs text-white/70 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/10"
                                                    aria-label={showPw ? "Skjul passord" : "Vis passord"}
                                                >
                                                    {showPw ? "Skjul" : "Vis"}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <label className="flex items-center gap-2 text-sm text-white/70 select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={remember}
                                                    onChange={(e) => setRemember(e.target.checked)}
                                                    className="h-4 w-4 rounded border-white/20 bg-black/40"
                                                />
                                                Husk meg
                                            </label>

                                            <button
                                                type="button"
                                                className="text-sm text-white/70 hover:text-white"
                                                onClick={() => setError("Demo: koble til «Glemt passord»-flyt.")}
                                            >
                                                Glemt passord?
                                            </button>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full rounded-2xl bg-orange-500/90 hover:bg-orange-500 px-4 py-3 font-semibold text-black transition disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {loading ? "Logger inn…" : "Logg inn"}
                                        </button>

                                        <div className="pt-2 text-xs text-white/55 leading-relaxed">
                                            Ved innlogging godtar du sikkerhets- og personvernpolicy for Chamcore.
                                        </div>
                                    </form>
                                </div>

                                {/* footer */}
                                <div className="px-6 py-4 border-t border-white/10 bg-black/20">
                                    <div className="flex items-center justify-between text-xs text-white/55">
                                        <span>© {new Date().getFullYear()} Chamcore</span>
                                        <span className="text-white/45">Secure • SSO-ready</span>
                                    </div>
                                </div>
                            </div>

                            {/* mobile brand header */}
                            <div className="lg:hidden mt-6 text-center">
                                <div className="text-xl font-semibold tracking-tight">
                                    <span className="opacity-90">{brand}</span>
                                    <span className="opacity-60">.chamcore.com</span>
                                </div>
                                <div className="text-sm text-white/65 mt-2">{subtitle}</div>
                            </div>

                            <div className="mt-6 text-center text-xs text-white/45">
                                Tips: legg inn egne car-bilder i <span className="text-white/70">/public/images/</span> og oppdater
                                <span className="text-white/70"> BG_IMAGES</span>.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
